import { GET_COMPANY } from "@apiUtils";
import { mutationFetch } from "@atlas/sdk";
import type {
  ICompanyApplicationTenant,
  ICompanyUserTenant,
  ILiteApplication,
} from "@models";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const USER_TENANTS_KEY = "userTenantsKey";
export const APPLICATION_TENANTS_KEY = "applicationTenantsKey";
export const LITE_APPLICATIONS_KEY = "liteApplicationsKey";

export const getUserTenantsKey = (
  companyUuid: string,
  userId: string,
  basePath?: string,
) => [USER_TENANTS_KEY, companyUuid, userId, basePath] as const;

export const getApplicationTenantsKey = (
  companyUuid: string,
  basePath?: string,
) => [APPLICATION_TENANTS_KEY, companyUuid, basePath] as const;

export const getLiteApplicationsKey = (
  companyUuid: string,
  userId: string,
  basePath?: string,
) => [LITE_APPLICATIONS_KEY, companyUuid, userId, basePath] as const;

export const useLiteApplications = (
  companyUuid: string | undefined,
  userId: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      ILiteApplication[],
      AxiosError,
      ILiteApplication[],
      ReturnType<typeof getLiteApplicationsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getLiteApplicationsKey(companyUuid ?? "", userId ?? "", basePath),
    queryFn: async () => {
      const path = `${GET_COMPANY}/${companyUuid}/user/${userId}/lite-applications`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
      const data = await mutationFetch<ILiteApplication[]>({
        url,
        method: "GET",
      });
      return data;
    },
    enabled: !!companyUuid && !!userId,
    ...options,
  });
};

export const useUserTenants = (
  companyUuid: string | undefined,
  userId: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      ICompanyUserTenant[],
      AxiosError,
      ICompanyUserTenant[],
      ReturnType<typeof getUserTenantsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getUserTenantsKey(companyUuid ?? "", userId ?? "", basePath),
    queryFn: async () => {
      const path = `${GET_COMPANY}/${companyUuid}/user/${userId}/tenants`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
      const data = await mutationFetch<ICompanyUserTenant[]>({
        url,
        method: "GET",
      });
      return data;
    },
    enabled: !!companyUuid && !!userId,
    ...options,
  });
};

export const useApplicationTenants = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      ICompanyApplicationTenant[],
      AxiosError,
      ICompanyApplicationTenant[],
      ReturnType<typeof getApplicationTenantsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getApplicationTenantsKey(companyUuid ?? "", basePath),
    queryFn: async () => {
      const path = `${GET_COMPANY}/${companyUuid}/application-tenants`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
      const data = await mutationFetch<ICompanyApplicationTenant[]>({
        url,
        method: "GET",
      });
      return data;
    },
    enabled: !!companyUuid,
    ...options,
  });
};
